import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProjects } from '@/hooks/useProjects.js';
import { useUrlTextFilter } from '@/hooks/useUrlFilterState.js';
import { Avatar } from '@/components/Avatar.js';
import { SearchInput } from '@/components/SearchInput.js';
import { PageHeader } from '@/components/PageHeader.js';
import { LoadingState, ErrorState, EmptyState } from '@/components/PageState.js';

export function ProjectList() {
  const { data: projects, isLoading, error } = useProjects();
  const [filter, setFilter] = useUrlTextFilter('filter');

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (!filter.trim()) return projects;
    const lower = filter.toLowerCase();
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(lower) ||
        p.key.toLowerCase().includes(lower)
    );
  }, [projects, filter]);

  if (isLoading) return <LoadingState label="Loading projects..." />;
  if (error) return <ErrorState message="Failed to load projects" />;

  return (
    <div>
      <PageHeader
        title="Projects"
        meta={projects ? `${projects.length}` : undefined}
        actions={<SearchInput value={filter} onChange={setFilter} placeholder="Filter by name or key..." className="w-full sm:w-72" />}
      />
      {filteredProjects.length === 0 && filter.trim() && (
        <EmptyState title={`No projects match "${filter}"`} />
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {filteredProjects.map((project) => (
          <Link
            key={project.key}
            to={`/projects/${project.key}`}
            className="group rounded-lg border bg-white p-4 transition-all hover:border-blue-300 hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              {project.avatarUrl ? (
                <img src={project.avatarUrl} alt="" className="h-10 w-10 shrink-0 rounded-md" />
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-100 text-sm font-bold text-blue-700">
                  {project.key.slice(0, 2)}
                </div>
              )}
              <div className="min-w-0">
                <h2 className="truncate font-semibold text-gray-900 group-hover:text-blue-700">{project.name}</h2>
                <p className="text-xs font-medium tracking-wide text-gray-500">{project.key}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between border-t pt-3 text-xs text-gray-500">
              {project.lead ? (
                <span className="flex min-w-0 items-center gap-2">
                  <Avatar name={project.lead.displayName} avatarUrl={project.lead.avatarUrl} size="sm" />
                  <span className="truncate">Lead: {project.lead.displayName}</span>
                </span>
              ) : <span />}
              <span className="shrink-0 text-blue-600 opacity-0 transition-opacity group-hover:opacity-100">View issues &rarr;</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
